import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Stethoscope, CheckCircle2 } from 'lucide-react';
import { apiService, Provider, ProviderRole } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const ROLE_OPTIONS: Array<{ value: ProviderRole; labelKey: string }> = [
  { value: 'dentist', labelKey: 'providers.roleDentist' },
  { value: 'hygienist', labelKey: 'providers.roleHygienist' },
];

const emptyProvider: Partial<Provider> = {
  name: '',
  email: '',
  phone: '',
  specialization: '',
  role: 'dentist',
  google_calendar_id: '',
  is_active: true,
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const ProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Provider> | null>(null);
  const [saving, setSaving] = useState(false);

  const roleLabel = (r: ProviderRole | undefined) => {
    const opt = ROLE_OPTIONS.find(o => o.value === r);
    return opt ? t(opt.labelKey) : r ?? '—';
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getProviders(false);
      setProviders(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || t('providers.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name || !editing.email) {
      setError(t('providers.errorNameEmail'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.id) {
        await apiService.updateProvider(editing.id, editing);
      } else {
        await apiService.createProvider(editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('providers.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: Provider) => {
    if (!window.confirm(t('providers.confirmDelete', { name: p.name }))) return;
    try {
      await apiService.deleteProvider(p.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('providers.errorDelete'));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('providers.eyebrow')}
        title={t('providers.title')}
        description={t('providers.pageDescription')}
        actions={
          <button
            onClick={() => setEditing({ ...emptyProvider })}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
            {t('providers.newProvider')}
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">{t('common.loading')}</div>
        ) : providers.length === 0 ? (
          <EmptyState
            icon={Stethoscope}
            title={t('providers.emptyTitle')}
            description={t('providers.emptyDescription')}
            action={
              <button
                onClick={() => setEditing({ ...emptyProvider })}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                {t('providers.emptyAction')}
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-border">
              <thead className="bg-brand-surface-2/60">
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('providers.role')}</Th>
                  <Th>{t('common.email')}</Th>
                  <Th>{t('common.phone')}</Th>
                  <Th>{t('providers.displaySpecialization')}</Th>
                  <Th>{t('providers.googleCalendar')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="right"><span className="sr-only">{t('common.actions')}</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {providers.map(p => (
                  <tr key={p.id} className={`hover:bg-brand-surface-2/60 transition-colors ${p.is_active ? '' : 'opacity-60'}`}>
                    <td className="px-4 py-3 text-sm font-medium text-brand-text">{p.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <StatusPill tone={p.role === 'hygienist' ? 'accent' : 'info'}>
                        {roleLabel(p.role)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.email}</td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.phone}</td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.specialization}</td>
                    <td className="px-4 py-3 text-sm">
                      {p.google_calendar_id ? (
                        <span
                          title={p.google_calendar_id}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-medium"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                          {t('providers.linked')}
                        </span>
                      ) : (
                        <span className="text-xs text-brand-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.is_active
                        ? <StatusPill tone="success" dot>{t('common.active')}</StatusPill>
                        : <StatusPill tone="neutral">{t('providers.inactive')}</StatusPill>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <IconButton onClick={() => setEditing({ ...p })} label={t('common.edit')} tone="primary">
                          <Pencil className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                        <IconButton onClick={() => onDelete(p)} label={t('common.delete')} tone="danger">
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border max-w-lg w-full p-6">
            <h3 className="text-base font-semibold mb-5 text-brand-text">
              {editing.id ? t('providers.editProvider') : t('providers.newProvider')}
            </h3>
            <div className="space-y-3">
              <Field label={t('providers.nameField')} value={editing.name || ''} onChange={v => setEditing({ ...editing, name: v })} />
              <Field label={t('providers.emailField')} type="email" value={editing.email || ''} onChange={v => setEditing({ ...editing, email: v })} />
              <Field label={t('common.phone')} value={editing.phone || ''} onChange={v => setEditing({ ...editing, phone: v })} />
              <Field label={t('providers.displaySpecialization')} value={editing.specialization || ''} onChange={v => setEditing({ ...editing, specialization: v })} />

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">{t('providers.roleRequired')}</label>
                <select
                  value={editing.role || 'dentist'}
                  onChange={e => setEditing({ ...editing, role: e.target.value as ProviderRole })}
                  className={inputClass}
                >
                  {ROLE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                  ))}
                </select>
                <p className="text-xs text-brand-text-muted mt-1">
                  {t('providers.roleHint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">{t('providers.googleCalendarId')}</label>
                <input
                  type="text"
                  value={editing.google_calendar_id || ''}
                  onChange={e => setEditing({ ...editing, google_calendar_id: e.target.value })}
                  placeholder={t('providers.googleCalendarIdPlaceholder')}
                  className={`${inputClass} font-mono`}
                />
                <p className="text-xs text-brand-text-muted mt-1">
                  {t('providers.googleCalendarIdHint')}
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                  className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-brand-border">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}>
    {children}
  </th>
);

const IconButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'primary' | 'danger';
}> = ({ children, onClick, label, tone }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400'
      : 'text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10';
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${toneClass}`}
    >
      {children}
    </button>
  );
};

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string }> = ({
  label,
  value,
  onChange,
  type = 'text',
}) => (
  <div>
    <label className="block text-sm font-medium text-brand-text mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={inputClass}
    />
  </div>
);

export default ProvidersPage;
